import { useEffect, useState, useMemo } from "react";
import { SocketFabric } from "shared/services/socket";
import { IUserModel } from "../../users";
import { access_token } from "entities/auth";
import { useStore, useEvent } from "effector-react";
import {
  $individualChatMessageStore,
  addMessage,
  removeMessage,
  updateUnreadMessagesInIndividualChat,
  updateUnreadMessagesInGroupChat,
  pinMessage as pinMessageEvent,
  unpinMessage as unpinMessageEvent,
} from "../model";
import { Message } from "../types";
import { AppStorage } from "shared/services/app-storage";

const useChatListWebSocket = () => {
  const socket = useMemo(
    () => new SocketFabric("wss://api.mama-api.ru/api/v1/chat/ws"),
    []
  );

  const [isConnected, setIsConnected] = useState(false);
  const [isError, setIsError] = useState("");
  const addNewMessage = useEvent(addMessage);
  const removeMessageEvent = useEvent(removeMessage);
  const updateUnreadIndividualChat = useEvent(
    updateUnreadMessagesInIndividualChat
  );
  const updateUnreadGroupChat = useEvent(updateUnreadMessagesInGroupChat);
  const pinMessageStoreEvent = useEvent(pinMessageEvent);
  const unpinMessageStoreEvent = useEvent(unpinMessageEvent);

  // Получаем текущее состояние сообщений для проверки дубликатов
  const currentMessages = useStore($individualChatMessageStore);

  // Получаем токен из локального хранилища
  const storage = useMemo(() => new AppStorage(), []);
  const token = storage.getItem("access_token");

  useEffect(() => {
    // Соединение установлено
    socket.onOpen = () => {
      console.log("WebSocket: соединение установлено успешно");
      setIsConnected(true);
    };

    // Получено сообщение
    socket.onMessage = (data) => {
      console.log("WebSocket: получены данные", data);

      // Проверяем на ошибку с токеном
      if (data && data.ok === false && data.error === "invalid token") {
        console.error(
          "WebSocket: недействительный токен, требуется повторная авторизация"
        );
        setIsError("invalid_token");
        return;
      }

      // Обработка события удаления сообщения
      if (data && data.event === "delete_message" && data.data) {
        try {
          console.log(
            "WebSocket: получено событие удаления сообщения",
            data.data
          );
          if (data.data.message_id) {
            removeMessageEvent(data.data.message_id);
            console.log(
              "WebSocket: сообщение удалено из стора",
              data.data.message_id
            );
          }
        } catch (error) {
          console.error(
            "WebSocket: ошибка при обработке удаления сообщения:",
            error
          );
        }
        return;
      }

      // Обработка события закрепления сообщения
      if (data && data.event === "pin_message" && data.data) {
        try {
          console.log(
            "WebSocket: получено событие закрепления сообщения",
            data.data
          );
          if (data.data.message_id) {
            pinMessageStoreEvent(data.data.message_id);
            console.log(
              "WebSocket: сообщение закреплено в сторе",
              data.data.message_id
            );
          }
        } catch (error) {
          console.error(
            "WebSocket: ошибка при обработке закрепления сообщения:",
            error
          );
        }
        return;
      }

      // Обработка события открепления сообщения
      if (data && data.event === "unpin_message" && data.data) {
        try {
          console.log(
            "WebSocket: получено событие открепления сообщения",
            data.data
          );
          if (data.data.message_id) {
            unpinMessageStoreEvent(data.data.message_id);
            console.log(
              "WebSocket: сообщение откреплено в сторе",
              data.data.message_id
            );
          }
        } catch (error) {
          console.error(
            "WebSocket: ошибка при обработке открепления сообщения:",
            error
          );
        }
        return;
      }

      // Обработка события прочтения сообщений
      if (data && data.event === "read_message" && data.data) {
        try {
          console.log(
            "WebSocket: получено событие прочтения сообщений",
            data.data
          );

          // Обновляем счетчик непрочитанных сообщений в соответствующем списке чатов
          if (data.data.chat_id) {
            if (data.type_chat === "group") {
              updateUnreadGroupChat(data.data.chat_id);
              console.log(
                "WebSocket: обновлен счетчик непрочитанных сообщений в групповом чате",
                data.data.chat_id
              );
            } else {
              updateUnreadIndividualChat(data.data.chat_id);
              console.log(
                "WebSocket: обновлен счетчик непрочитанных сообщений в индивидуальном чате",
                data.data.chat_id
              );
            }
          }
        } catch (error) {
          console.error(
            "WebSocket: ошибка при обработке прочтения сообщений:",
            error
          );
        }
        return;
      }

      // Проверяем, что данные имеют правильную структуру и содержат сообщение
      if (data && data.event === "message" && data.data) {
        try {
          // Формируем объект сообщения из полученных данных
          const newMessage: Message = {
            id: data.data.id || Date.now().toString(), // Используем id из данных или генерируем временный
            chat_id: data.data.chat_id,
            sender_id: data.data.sender_id,
            text: data.data.message,
            created_at: data.data.created_at || new Date().toISOString(),
            is_pinned: data.data.is_pinned || false, // Добавляем информацию о закрепленности сообщения
          };

          console.log("WebSocket: получено новое сообщение", {
            id: newMessage.id,
            chat_id: newMessage.chat_id,
            sender_id: newMessage.sender_id,
            text: newMessage.text,
            created_at: newMessage.created_at,
            is_pinned: newMessage.is_pinned,
          });

          // Проверяем, не является ли сообщение дубликатом
          const isDuplicate = currentMessages?.messages.some(
            (msg) => msg.id === newMessage.id
          );

          if (!isDuplicate) {
            console.log("WebSocket: добавляем новое сообщение в стор");
            addNewMessage(newMessage);
          } else {
            console.log(
              "WebSocket: сообщение уже существует в сторе, пропускаем"
            );
          }
        } catch (error) {
          console.error(
            "WebSocket: ошибка при обработке входящего сообщения:",
            error
          );
        }
      } else {
        console.log(
          "WebSocket: получены данные неизвестного формата или не сообщение"
        );
      }
    };

    // Соединение закрыто
    socket.onClose = (event) => {
      setIsConnected(false);
      if (event.wasClean) {
        console.log("WebSocket: соединение закрыто чисто");
      } else {
        console.log("WebSocket: обрыв соединения");
      }
      console.log(`WebSocket: код: ${event.code}, причина: ${event.reason}`);
    };

    // Ошибка соединения
    socket.onError = (error) => {
      console.log("WebSocket: соединение не установлено");
      console.error("WebSocket: ошибка", error);
      setIsError(error.type);
    };
  }, [
    isConnected,
    socket,
    addNewMessage,
    removeMessageEvent,
    updateUnreadIndividualChat,
    updateUnreadGroupChat,
    pinMessageStoreEvent,
    unpinMessageStoreEvent,
    currentMessages,
  ]);

  const sendMessage = (
    message: string,
    chatId: string,
    typeChat: "solo" | "group",
    event: "message" | "join",
    replyTo?: string
  ) => {
    // Создаем объект сообщения с правильной структурой
    const messageData: {
      event: string;
      type_chat: string;
      data: {
        access_token: string;
        chat_id: string;
        message: string;
        reply?: string;
      };
    } = {
      event: event,
      type_chat: typeChat,
      data: {
        access_token: token ? `Bearer ${token}` : "",
        chat_id: chatId,
        message: message,
      },
    };

    // Добавляем reply, если указан
    if (replyTo) {
      messageData.data.reply = replyTo;
    }

    console.log("WebSocket: отправка сообщения", messageData);
    socket.sendMessage(JSON.stringify(messageData));
  };

  // Функция для удаления сообщения
  const deleteMessage = (
    messageId: string,
    chatType: "solo" | "group" = "solo"
  ) => {
    const deleteData = {
      event: "delete_message",
      type_chat: chatType, // Используем переданный тип чата
      data: {
        access_token: token ? `Bearer ${token}` : "",
        message_id: messageId,
      },
    };

    console.log("WebSocket: отправка запроса на удаление сообщения", {
      ...deleteData,
      data: {
        ...deleteData.data,
        access_token: token ? "Bearer [ТОКЕН]" : "Токен отсутствует", // Скрываем токен в логах
      },
    });

    try {
      socket.sendMessage(JSON.stringify(deleteData));
      console.log("WebSocket: запрос на удаление сообщения отправлен");

      // Оптимистично удаляем сообщение из локального стора
      removeMessageEvent(messageId);
    } catch (error) {
      console.error(
        "WebSocket: ошибка при отправке запроса на удаление",
        error
      );
      throw error;
    }
  };

  // Функция для закрепления сообщения
  const pinMessage = (
    messageId: string,
    chatId: string,
    chatType: "solo" | "group" = "solo"
  ) => {
    const pinData = {
      event: "pin_message",
      type_chat: chatType,
      data: {
        access_token: token ? `Bearer ${token}` : "",
        message_id: messageId,
        chat_id: chatId,
      },
    };

    console.log("WebSocket: отправка запроса на закрепление сообщения", {
      ...pinData,
      data: {
        ...pinData.data,
        access_token: token ? "Bearer [ТОКЕН]" : "Токен отсутствует",
      },
    });

    try {
      // Отправляем запрос через WebSocket
      socket.sendMessage(JSON.stringify(pinData));
      console.log("WebSocket: запрос на закрепление сообщения отправлен");

      // Оптимистично обновляем сообщение в локальном сторе
      pinMessageStoreEvent(messageId);

      // Добавляем обработку ошибки от сервера, если сервер не поддерживает закрепление
      // Если через 2 секунды нет ответа от сервера, считаем что функционал работает только локально
      setTimeout(() => {
        // Здесь можно добавить проверку, был ли получен ответ от сервера
        console.log(
          "Информация: закрепление сообщения может работать только локально"
        );
      }, 2000);
    } catch (error) {
      console.error(
        "WebSocket: ошибка при отправке запроса на закрепление сообщения",
        error
      );
      throw error;
    }
  };

  // Функция для открепления сообщения
  const unpinMessage = (
    messageId: string,
    chatId: string,
    chatType: "solo" | "group" = "solo"
  ) => {
    const unpinData = {
      event: "unpin_message",
      type_chat: chatType,
      data: {
        access_token: token ? `Bearer ${token}` : "",
        message_id: messageId,
        chat_id: chatId,
      },
    };

    console.log("WebSocket: отправка запроса на открепление сообщения", {
      ...unpinData,
      data: {
        ...unpinData.data,
        access_token: token ? "Bearer [ТОКЕН]" : "Токен отсутствует",
      },
    });

    try {
      // Отправляем запрос через WebSocket
      socket.sendMessage(JSON.stringify(unpinData));
      console.log("WebSocket: запрос на открепление сообщения отправлен");

      // Оптимистично обновляем сообщение в локальном сторе
      unpinMessageStoreEvent(messageId);

      // Добавляем обработку ошибки от сервера, если сервер не поддерживает открепление
      // Если через 2 секунды нет ответа от сервера, считаем что функционал работает только локально
      setTimeout(() => {
        // Здесь можно добавить проверку, был ли получен ответ от сервера
        console.log(
          "Информация: открепление сообщения может работать только локально"
        );
      }, 2000);
    } catch (error) {
      console.error(
        "WebSocket: ошибка при отправке запроса на открепление сообщения",
        error
      );
      throw error;
    }
  };

  // Функция для отметки всех сообщений в чате как прочитанных
  const readMessages = (
    chatId: string,
    chatType: "solo" | "group" = "solo"
  ) => {
    const readData = {
      event: "read_message",
      type_chat: chatType,
      data: {
        access_token: token ? `Bearer ${token}` : "",
        chat_id: chatId,
      },
    };

    console.log("WebSocket: отправка запроса на прочтение сообщений", {
      ...readData,
      data: {
        ...readData.data,
        access_token: token ? "Bearer [ТОКЕН]" : "Токен отсутствует", // Скрываем токен в логах
      },
    });

    try {
      socket.sendMessage(JSON.stringify(readData));
      console.log("WebSocket: запрос на прочтение сообщений отправлен");

      // Оптимистично обновляем счетчик непрочитанных сообщений в UI
      if (chatType === "group") {
        updateUnreadGroupChat(chatId);
      } else {
        updateUnreadIndividualChat(chatId);
      }
    } catch (error) {
      console.error(
        "WebSocket: ошибка при отправке запроса на прочтение сообщений",
        error
      );
      throw error;
    }
  };

  return {
    isConnected,
    sendMessage,
    deleteMessage,
    readMessages,
    pinMessage,
    unpinMessage,
    isError,
  };
};

export { useChatListWebSocket };
